/**
 * Sitzungsentzug: nach einem serverseitigen Sitzungsende neu laden, statt sich
 * im ALTEN Bundle neu anzumelden.
 *
 * ─── Warum es dieses Modul gibt ─────────────────────────────────────────────
 * Am 07.09.2026 hat ein Tab, der seit dem 19.08. offen stand, im Projekt
 * 26051026 alte Zaehler ueber frische Buchungen zurueckgeschrieben. Damit
 * solche Tabs nicht ewig auf einem alten Build weiterlaufen, werden Sitzungen
 * kuenftig serverseitig beendet (Zeilen aus auth.sessions, naechtlich).
 *
 * Der alte Tab merkt das am fehlgeschlagenen Refresh: supabase-js meldet
 * SIGNED_OUT, App.tsx setzt `session` auf null und zeigt die Anmeldemaske —
 * aber eben im alten Bundle. Wer sich dort neu anmeldet, arbeitet mit genau
 * dem Build weiter, dessentwegen die Sitzung beendet wurde. Das Loch schliesst
 * ein erzwungenes Neuladen im Widerrufsfall: die Anmeldung laeuft danach immer
 * auf dem frischen Bundle.
 *
 * Neu geladen wird NUR im Widerrufsfall. Eine Abmeldung, die aus dieser App
 * kommt (Abmelde-Knopf, `signOut` des Anmeldebildschirms nach der
 * Registrierung), ist kein Widerruf — dort ist der Bundle-Stand unveraendert
 * in Ordnung, und ein Reload wuerde nur die gerade eingetippte Eingabe
 * wegwerfen.
 *
 * Reines Modul: kein React, kein DOM. Speicher und Zeit kommen herein, damit
 * die Regel ohne Browser pruefbar ist (Hausmuster: versionGate.ts,
 * idleHold.ts, projectLockLogic.ts).
 */

/** Was nach einem Auth-Ereignis geschehen soll. */
export type SignedOutDecision = 'reload' | 'none';

/**
 * Schleifenschutz — Zuschnitt wie `versionGate.shouldAutoReload`, aber mit
 * eigenem Schluessel UND in einem anderen Speicher.
 *
 * Bewusst sessionStorage und NICHT localStorage: der Schutz gilt je TAB. Ein
 * serverseitiges Sitzungsende trifft alle offenen Tabs gleichzeitig; laegen
 * sie auf einem gemeinsamen Zeitstempel, lade der erste neu und alle uebrigen
 * blieben genau in dem alten Bundle stehen, gegen das dieses Modul gebaut ist.
 * sessionStorage ueberlebt den Reload und stirbt mit dem Tab — genau die
 * Lebensdauer, die der Schutz braucht.
 */
export const REVOCATION_RELOAD_KEY = 'sessionRevocation.lastReload';

/** Merkzeichen fuer die Anmeldemaske; sessionStorage ueberlebt den Reload und stirbt mit dem Tab. */
export const REVOCATION_NOTICE_KEY = 'osia_session_revoked_v1';

/** Hoechstens ein erzwungener Reload je Minute und Tab. */
export const REVOCATION_WINDOW_MS = 60_000;

/** Obergrenze der Wartezeit vor dem Reload (Begruendung bei `warteAufRuhe`). */
export const REVOCATION_MAX_WAIT_MS = 2_000;

/**
 * Der Satz, den die Anmeldemaske nach dem erzwungenen Reload zeigt.
 *
 * Er sagt beides: warum die Maske da ist, und dass nichts verloren ist. Der
 * zweite Halbsatz ist keine Beruhigung, sondern die Wahrheit ueber die
 * Offline-Warteschlange (IndexedDB osia_offline_v1) — sie ueberlebt den
 * Reload und wird nach der Anmeldung hochgeladen.
 */
export const REVOCATION_NOTICE =
    'Ihre Sitzung wurde beendet. Bitte melden Sie sich neu an — '
    + 'lokal gesicherte Eingaben werden danach hochgeladen.';

// ─── Merkzeichen: kam die Abmeldung aus dieser App? ─────────────────────────
//
// Modulweite Flagge und keine Prop-Kette: `handleLogout` sitzt in App.tsx, der
// zweite `signOut`-Aufruf im Anmeldebildschirm (nach der Registrierung) drei
// Ebenen daneben. Beide setzen dasselbe Merkzeichen, bevor sie abmelden — der
// Entscheider liest es und sieht: kein Widerruf.
let lokaleAbmeldung = false;

/** Setzen. Nur `abmelden` und Tests rufen das direkt auf. */
export const merkeLokaleAbmeldung = (): void => { lokaleAbmeldung = true; };

/** Zuruecknehmen — beim Eintreffen einer neuen Sitzung und nach dem Auswerten. */
export const vergissLokaleAbmeldung = (): void => { lokaleAbmeldung = false; };

/** Steht das Merkzeichen gerade? */
export const istLokaleAbmeldung = (): boolean => lokaleAbmeldung;

export interface AbmeldeErgebnis {
    /** Gesetzt, wenn `signOut` gescheitert ist, OHNE zu werfen. */
    error?: unknown;
}

/**
 * Der EINZIGE Weg, sich abzumelden — Merkzeichen setzen, abmelden, Merkzeichen
 * zuruecknehmen.
 *
 * Warum ein Wrapper und nicht zwei Zeilen an zwei Stellen: ohne `finally`
 * bleibt das Merkzeichen stehen, wenn `signOut()` wirft, ohne ein
 * Auth-Ereignis ausgeloest zu haben (Netzfehler vor dem Entfernen der
 * Sitzung). Der Tab haelt sich dann fuer die Dauer seines Lebens selbst fuer
 * "hat gerade selbst abgemeldet" und verschluckt den naechsten ECHTEN Entzug.
 * Beide Abmeldewege (Abmelde-Knopf in App.tsx, `signOut` nach der
 * Registrierung im Anmeldebildschirm) gehen deshalb hier durch.
 *
 * Dass das `finally` nicht zu frueh greift, ist belegt und keine Annahme:
 * `GoTrueClient._removeSession()` wartet auf
 * `_notifyAllSubscribers('SIGNED_OUT', null)`
 * (node_modules/@supabase/auth-js/dist/module/GoTrueClient.js:1556–1559), und
 * `_notifyAllSubscribers` wartet seinerseits auf JEDEN Abonnenten-Callback
 * (ebenda :1525–1533). Die Auswertung in App.tsx laeuft also, waehrend
 * `signOut()` noch offen ist — das Zuruecknehmen danach kommt nie zu frueh.
 */
export const abmelden = async (
    signOut: () => Promise<AbmeldeErgebnis | undefined>,
): Promise<AbmeldeErgebnis> => {
    merkeLokaleAbmeldung();
    try {
        // Der Rueckgabewert wird DURCHGEREICHT und nicht geprueft: was mit
        // einem `{ error }` geschieht, entscheidet der Aufrufer — er allein
        // weiss, welchen Oberflaechenzustand er gerade nicht setzen darf.
        const ergebnis = await signOut();
        return ergebnis ?? {};
    } finally {
        vergissLokaleAbmeldung();
    }
};

/**
 * Die Meldung, wenn das Abmelden gar nicht erst durchkam.
 *
 * `supabase.auth.signOut()` WIRFT in diesem Fall nicht, sondern liefert
 * `{ error }` zurück — und zwar bevor es die Sitzung lokal abbaut. Es gibt
 * dann kein SIGNED_OUT, die Sitzung lebt weiter, und die Oberflaeche darf
 * nicht behaupten, man sei abgemeldet: wer den Rechner in dem Glauben
 * verlaesst, laesst eine offene Sitzung stehen.
 */
export const ABMELDEN_FEHLGESCHLAGEN =
    'Abmelden fehlgeschlagen — keine Verbindung. Sie sind weiterhin angemeldet.';

/**
 * Der Speicher dieses Tabs, abgesichert.
 *
 * Nicht nur der Zugriff auf einen Schluessel kann werfen, sondern schon der
 * Getter `window.sessionStorage`: bei blockierten Seitendaten wirft er einen
 * SecurityError. Stuende er ungeschuetzt im Auth-Callback, risse er den
 * ganzen Abmelde-Zweig mit — deshalb geht JEDE Stelle ueber diesen Helfer.
 */
export const tabSpeicher = (): Storage | null => {
    try {
        if (typeof window === 'undefined') return null;
        return window.sessionStorage ?? null;
    } catch {
        return null;
    }
};

/**
 * Darf JETZT neu geladen werden?
 *
 * Schreibt den Zeitstempel beim Ja gleich mit: der Schutz muss auch dann
 * stehen, wenn der Reload sofort folgt und kein weiterer Code mehr laeuft.
 * Faellt der Speicher aus (privater Modus, gesperrte Seitendaten), ist die
 * Antwort NEIN — ein Reload ohne Gedaechtnis ist der Anfang einer Schleife.
 */
export const darfNeuLaden = (
    storage: Pick<Storage, 'getItem' | 'setItem'>,
    nowMs: number,
    windowMs: number = REVOCATION_WINDOW_MS,
): boolean => {
    try {
        const letzter = Number(storage.getItem(REVOCATION_RELOAD_KEY));
        if (Number.isFinite(letzter) && letzter > 0 && nowMs - letzter < windowMs) return false;
        storage.setItem(REVOCATION_RELOAD_KEY, String(nowMs));
        return true;
    } catch {
        return false;
    }
};

export interface SignedOutInput {
    /** Das Ereignis von supabase-js, woertlich durchgereicht. */
    event: string;
    /** Hatte dieser Tab in diesem Lauf ueberhaupt schon eine Sitzung? */
    hadSession: boolean;
    /** Kam die Abmeldung aus dieser App (Merkzeichen oben)? */
    localLogout: boolean;
    /** Laeuft in diesem Tab gerade ein Passwort-Zuruecksetzen? */
    passwordRecovery?: boolean;
    /** Der Speicher DIESES Tabs (`tabSpeicher()`); fehlt er, wird nicht neu geladen. */
    storage: Pick<Storage, 'getItem' | 'setItem'> | null | undefined;
    nowMs: number;
    /** Sperrfenster des Schleifenschutzes. Default REVOCATION_WINDOW_MS. */
    windowMs?: number;
}

/**
 * Die ganze Regel an einer Stelle.
 *
 * 'reload' nur, wenn ALLE vier Bedingungen stimmen:
 *   1. Das Ereignis ist wirklich SIGNED_OUT. INITIAL_SESSION ohne Sitzung,
 *      TOKEN_REFRESHED, USER_UPDATED — nichts davon ist ein Widerruf.
 *   2. Der Tab hatte in diesem Lauf eine Sitzung. Sonst stuende beim Start
 *      jedes nicht angemeldeten Besuchers ein Reload an.
 *   3. Die Abmeldung kam nicht aus dieser App.
 *   4. Der Tab ist nicht im Passwort-Zuruecksetzen. Dieser Weg meldet
 *      planmaessig ab und braucht seine eigene Maske.
 * Und danach der Schleifenschutz: hoechstens einmal je Fenster.
 */
export const decideOnSignedOut = (input: SignedOutInput): SignedOutDecision => {
    const {
        event, hadSession, localLogout, passwordRecovery = false,
        storage, nowMs, windowMs = REVOCATION_WINDOW_MS,
    } = input;
    if (event !== 'SIGNED_OUT') return 'none';
    if (!hadSession) return 'none';
    if (localLogout) return 'none';
    if (passwordRecovery) return 'none';
    if (!storage) return 'none';
    return darfNeuLaden(storage, nowMs, windowMs) ? 'reload' : 'none';
};

/** Merkzeichen fuer die Anmeldemaske setzen. Faellt der Speicher aus, faellt nur der Hinweis aus. */
export const merkeSitzungsende = (storage: Pick<Storage, 'setItem'> | null | undefined): void => {
    try {
        storage?.setItem(REVOCATION_NOTICE_KEY, '1');
    } catch {
        // Ohne Hinweis, aber mit Reload — der Reload ist der Zweck.
    }
};

/**
 * Merkzeichen abholen UND loeschen — einmal zeigen, nicht bei jedem Rendern.
 * Rueckgabe: der Hinweistext oder null.
 */
export const nimmSitzungsendeHinweis = (
    storage: Pick<Storage, 'getItem' | 'removeItem'> | null | undefined,
): string | null => {
    try {
        if (!storage?.getItem(REVOCATION_NOTICE_KEY)) return null;
        storage.removeItem(REVOCATION_NOTICE_KEY);
        return REVOCATION_NOTICE;
    } catch {
        return null;
    }
};

export interface WarteOptionen {
    obergrenzeMs?: number;
    taktMs?: number;
    now?: () => number;
    schlafe?: (ms: number) => Promise<void>;
}

/**
 * Gebundene Wartezeit vor dem Reload.
 *
 * ─── Warum ueberhaupt gewartet wird ─────────────────────────────────────────
 * Jede Eingabe am Bericht geht sofort in die Offline-Warteschlange
 * (App.handleUpdateReport → useOfflineSync.enqueue → IndexedDB). Der
 * Schreibvorgang ist eine Transaktion und laeuft asynchron; ein Dokument, das
 * mittendrin entladen wird, bricht sie ab. Dasselbe gilt fuer das Hochladen:
 * der Flush setzt den Eintrag vorher auf 'syncing' und erst hinterher wieder
 * auf 'pending' — wird dazwischen entladen, bleibt er in 'syncing' liegen, und
 * `canFlush` nimmt ihn nie wieder mit (offlineQueueLogic.ts).
 *
 * Deshalb wird vor dem Reload gewartet, bis die Warteschlange ruhig ist — aber
 * gebunden: nach `obergrenzeMs` wird trotzdem geladen. Ein Tab, der wegen
 * eines haengenden Uploads NICHT neu laedt, ist genau der Tab, gegen den
 * dieses Modul gebaut wurde.
 *
 * Rueckgabe: true = ruhig geworden, false = Obergrenze abgelaufen.
 */
export const warteAufRuhe = async (
    istBeschaeftigt: () => boolean,
    opt: WarteOptionen = {},
): Promise<boolean> => {
    const {
        obergrenzeMs = REVOCATION_MAX_WAIT_MS,
        taktMs = 50,
        now = Date.now,
        schlafe = (ms: number) => new Promise<void>(r => setTimeout(r, ms)),
    } = opt;
    const ende = now() + obergrenzeMs;
    for (;;) {
        let beschaeftigt: boolean;
        // Wirft die Abfrage, wird nicht gewartet: der Reload ist wichtiger als
        // eine Auskunft, die es nicht gibt.
        try {
            beschaeftigt = istBeschaeftigt();
        } catch {
            return true;
        }
        if (!beschaeftigt) return true;
        if (now() >= ende) return false;
        await schlafe(taktMs);
    }
};
